<div class="company-cases-page container-fluid">
  <div class="company-cases-shell d-flex flex-column mx-auto position-relative">
    <blocking-loader-overlay
      :show="page_loading"
      :message="page_loading_message"
      :detail="page_loading_detail"
    ></blocking-loader-overlay>
    <section class="company-cases-header">
      <div class="company-cases-hero">
        <div class="row g-2 align-items-start">
          <div class="col">
            <div class="company-cases-breadcrumb text-uppercase d-flex align-items-center gap-2">
              <span>Handler</span>
              <span>/</span>
              <span>Cases</span>
            </div>
            <h1 class="company-cases-title">Case queue</h1>
          </div>
        </div>
      </div>

      <section class="row g-3 company-cases-metrics">
        <div v-for="metric in metrics" :key="metric.label" class="col-12 col-sm-6 col-xl-3">
          <article class="company-cases-metric h-100 shadow">
            <div class="company-cases-metric-label text-uppercase">{{ metric.label }}</div>
            <div class="company-cases-metric-value">{{ metric.value }}</div>
            <div class="company-cases-metric-change" :class="metric.change_class">{{ metric.change }}</div>
          </article>
        </div>
      </section>
    </section>

    <section class="company-cases-list">
      <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
        <h2 class="company-cases-section-title mb-0">All Cases</h2>

        <div class="d-flex align-items-center gap-2">
          <button type="button" class="company-cases-new-btn" @click="export_cases_csv">Export CSV</button>
          <button type="button" class="company-cases-new-btn" @click="open_new_case_modal">+ New Case</button>
        </div>
      </div>

      <div class="company-cases-filters d-flex flex-wrap gap-2">
        <button
          v-for="filter in display_filters"
          :key="filter.key"
          type="button"
          class="company-cases-filter shadow-sm"
          :class="{ 'is-active': active_filter === filter.key }"
          @click="set_filter(filter.key)"
        >
          {{ filter.label }}
        </button>

        <select
          id="company_cases_status_filter"
          class="company-cases-filter company-cases-filter-select shadow-sm"
          :value="active_status_filter"
          @change="set_status_filter($event.target.value)"
        >
          <option
            v-for="status in display_status_filters"
            :key="status.key"
            :value="status.key"
          >
            {{ status.label }}
          </option>
        </select>
      </div>

      <div class="company-cases-table-wrap shadow">
        <div class="table-responsive">
          <table class="table company-cases-table align-middle mb-0">
            <thead>
              <tr>
                <th>Case</th>
                <th>Domain</th>
                <th>Status</th>
                <th>Risk</th>
                <th>Owner</th>
                <th>SLA</th>
                <th class="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in paginated_cases" :key="item.case_code" class="case-clickable-row" @click="go_to_case_detail(item)">
                <td>
                  <div class="company-case-code text-uppercase">{{ item.case_code }}</div>
                  <div class="company-case-title">{{ item.title }}</div>
                  <div class="company-case-subtitle">{{ item.subtitle }}</div>
                </td>
                <td>
                  <span class="company-case-domain" :class="item.domain_badge_class">
                    {{ item.domain }}
                  </span>
                </td>
                <td>
                  <div class="company-case-status d-flex align-items-center gap-2">
                    <span class="status-dot-sm rounded-circle d-inline-block" :class="item.status_color ? '' : item.status_dot_class" :style="item.status_dot_style"></span>
                    <span>{{ item.status }}</span>
                  </div>
                </td>
                <td>
                  <span class="company-case-risk" :class="item.risk_badge_class">
                    {{ item.risk_score }} <span class="company-case-risk-separator">·</span> {{ item.risk_label }}
                  </span>
                </td>
                <td>
                  <div class="company-case-owner">{{ item.owner }}</div>
                </td>
                <td>
                  <span class="company-case-sla" :class="item.sla_class">
                    <i v-if="item.sla_icon" class="bi" :class="item.sla_icon"></i>
                    <span class="company-case-sla-text">{{ item.sla }}</span>
                  </span>
                </td>
                <td class="text-end">
                  <div class="company-case-actions">
                    <button
                      type="button"
                      class="company-case-view-btn"
                      @click.stop="go_to_case_detail(item)"
                    >
                      View
                    </button>
                    <button
                      v-if="can_route_cases"
                      type="button"
                      class="company-case-route-btn"
                      @click.stop="open_route_case_modal(item)"
                    >
                      Route To
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="cases_error">
                <td colspan="7" class="py-4 text-center text-secondary">{{ cases_error }}</td>
              </tr>
              <tr v-else-if="!loading_cases && !filtered_cases.length">
                <td colspan="7" class="py-4 text-center text-secondary">No cases match this filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <section
        v-if="!loading_cases && filtered_cases.length"
        class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 px-2 py-3"
      >
        <div class="text-secondary text-xs">
          {{ pagination_summary }}
        </div>

        <nav aria-label="Cases pagination">
          <div class="d-inline-flex align-items-center rounded-3 border overflow-hidden bg-white">
            <button
              type="button"
              class="btn btn-sm btn-light border-0 rounded-0 px-2"
              @click="previous_page"
              :disabled="!can_go_previous"
              aria-label="Previous page"
            >
              <i class="bi bi-chevron-left"></i>
            </button>

            <button
              v-for="page in visible_page_numbers"
              :key="page"
              type="button"
              class="btn btn-sm rounded-0 border-0 border-start px-3 pagination-seg-btn"
              :class="page === current_page ? 'btn-primary-dark text-white' : 'btn-light'"
              @click="set_page(page)"
            >
              {{ page }}
            </button>

            <button
              type="button"
              class="btn btn-sm btn-light border-0 border-start rounded-0 px-2"
              @click="next_page"
              :disabled="!can_go_next"
              aria-label="Next page"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </nav>
      </section>
    </section>
  </div>

  <div v-if="show_new_case_modal" class="company-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_new_case_modal">
    <section class="company-case-modal bg-white position-relative">
      <blocking-loader-overlay
        :show="modal_loading"
        :message="modal_loading_message"
        :detail="modal_loading_detail"
      ></blocking-loader-overlay>
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">New case</h2>
          <div class="company-case-modal-copy">The same intake form serves every domain - the schema below is per-tenant</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close modal" @click="close_new_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="company_case_domain">Domain</label>
          <select
            id="company_case_domain"
            v-model="new_case_form.entity_type_id"
            class="form-select company-case-modal-control"
            :disabled="submitting_new_case || created_case || !new_case_domain_options.length"
            @change="prepare_new_case_onboarding"
          >
            <option value="" disabled>Select a domain</option>
            <option v-for="domain in new_case_domain_options" :key="domain.id" :value="String(domain.id)">
              {{ domain.label }}
            </option>
          </select>
        </div>

        <div v-if="new_case_error" class="small text-danger fw-semibold mb-3">
          {{ new_case_error }}
        </div>

        <div v-if="!loading_cases && !new_case_domain_options.length" class="small text-secondary mb-3">
          No entity types are available for this project yet.
        </div>

        <section class="company-case-onboarding-preview mb-4">
          <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
            <div>
              <div class="company-case-modal-label text-uppercase mb-1">Requestor preview</div>
              <div class="company-case-onboarding-copy">
                This is the intake structure the requestor will see for the selected domain.
              </div>
            </div>
            <span v-if="onboarding_preview" class="company-case-onboarding-version">
              Version {{ onboarding_preview.version }}
            </span>
          </div>

          <div v-if="onboarding_preview_error && !onboarding_preview_loading" class="company-case-onboarding-state is-error">
            <span>{{ onboarding_preview_error }}</span>
            <button type="button" class="btn btn-sm btn-light border" :disabled="onboarding_preview_loading" @click="prepare_new_case_onboarding">
              Try again
            </button>
          </div>

          <div v-else-if="onboarding_preview">
            <div class="company-case-onboarding-summary">
              <div>
                <strong>{{ onboarding_preview_forms.length }}</strong>
                <span>Forms</span>
              </div>
              <div>
                <strong>{{ onboarding_preview.workflows?.length || 0 }}</strong>
                <span>Workflows</span>
              </div>
              <div>
                <strong>{{ onboarding_preview.risk_assessments?.length || 0 }}</strong>
                <span>Risk assessments</span>
              </div>
              <div>
                <strong>{{ onboarding_preview.documents?.length || 0 }}</strong>
                <span>Documents</span>
              </div>
            </div>

            <div v-if="onboarding_preview_forms.length" class="company-case-onboarding-forms">
              <details
                v-for="(form, form_index) in onboarding_preview_forms"
                :key="form.form_id"
                class="company-case-onboarding-form"
                :open="form_index === 0"
              >
                <summary>
                  <span>{{ form.form_name }}</span>
                  <span>{{ form.sections?.length || 0 }} sections</span>
                </summary>
                <div class="company-case-onboarding-sections">
                  <div
                    v-for="section in form.sections || []"
                    :key="`${form.form_id}-${section.section_id}`"
                    class="company-case-onboarding-section"
                  >
                    <div class="company-case-onboarding-section-title">{{ section.section_name }}</div>
                    <div
                      v-for="question in section.questions || []"
                      :key="question.section_question_id"
                      class="company-case-onboarding-question"
                    >
                      <span>{{ question.question_text }}</span>
                      <span v-if="question.is_mandatory" class="company-case-onboarding-required">Required</span>
                    </div>
                  </div>
                </div>
              </details>
            </div>
            <div v-else class="company-case-onboarding-state">
              No requestor forms are configured for this domain.
            </div>
          </div>

          <div v-else class="company-case-onboarding-state">
            Select a domain to preview its requestor intake.
          </div>
        </section>

        <div v-if="!created_case" class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="company_case_requester">Requester / Claimant</label>
          <input
            id="company_case_requester"
            v-model.trim="new_case_form.requester"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Name or ID"
            :disabled="submitting_new_case"
          >
        </div>

        <div v-if="!created_case && is_email_source" class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="company_case_requester_email">Requester email</label>
          <input
            id="company_case_user_email"
            v-model.trim="new_case_form.user_email"
            type="email"
            class="form-control company-case-modal-control"
            placeholder="user@example.com"
            :disabled="submitting_new_case"
          >
        </div>

        <div v-if="!created_case" class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="company_case_description">Short description</label>
          <textarea
            id="company_case_description"
            v-model.trim="new_case_form.description"
            rows="4"
            class="form-control company-case-modal-control company-case-modal-textarea"
            placeholder="One line. e.g. Suspicious motor claim, 3rd this year..."
            :disabled="submitting_new_case"
          ></textarea>
        </div>

        <div v-if="!created_case" class="row g-3">
          <div class="col-12 col-md-6">
            <label class="company-case-modal-label text-uppercase mb-2" for="company_case_value">Value (optional)</label>
            <input
              id="company_case_value"
              v-model.trim="new_case_form.value"
              type="text"
              class="form-control company-case-modal-control"
              placeholder="R 0"
              :disabled="submitting_new_case"
            >
          </div>
          <div class="col-12 col-md-6">
            <label class="company-case-modal-label text-uppercase mb-2" for="company_case_source">Source channel</label>
            <select id="company_case_source" v-model="new_case_form.source_channel" class="form-select company-case-modal-control" :disabled="submitting_new_case">
              <option>Email</option>
              <option>Portal</option>
              <option>Phone</option>
              <option>Walk-in</option>
              <option>System import</option>
            </select>
          </div>
        </div>

        <section v-if="created_case" class="company-case-onboarding-result">
          <div class="company-case-onboarding-result-icon">
            <i class="bi bi-check2"></i>
          </div>
          <div>
            <div class="company-case-onboarding-result-title">Case created and onboarding executed</div>
            <div class="company-case-onboarding-copy">
              {{ created_case.case_code }} ·
              {{ onboarding_execution?.steps_executed?.length || 0 }} steps executed
            </div>
          </div>

          <div v-if="onboarding_status" class="company-case-onboarding-progress">
            <span :class="{ 'is-complete': onboarding_completion.forms_complete }">Forms</span>
            <span :class="{ 'is-complete': onboarding_completion.workflow_complete }">Workflow</span>
            <span :class="{ 'is-complete': onboarding_completion.documents_complete }">Documents</span>
            <span :class="{ 'is-complete': onboarding_completion.invoicing_complete }">Invoicing</span>
          </div>
        </section>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="submitting_new_case" @click="close_new_case_modal">
          {{ created_case ? 'Close' : 'Cancel' }}
        </button>
        <button
          v-if="!created_case"
          type="button"
          class="btn btn-primary-dark px-4"
          :disabled="submitting_new_case || !can_create_new_case"
          @click="create_new_case"
        >
          {{ submitting_new_case ? 'Running onboarding...' : 'Create case & run onboarding' }}
        </button>
        <button v-else type="button" class="btn btn-primary-dark px-4" @click="go_to_created_case">
          View case
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_route_case_modal" class="company-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_route_case_modal">
    <section class="company-case-modal company-case-route-modal bg-white">
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">{{ route_case_title }}</h2>
          <div class="company-case-modal-copy">{{ route_case_subtitle }}</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close route modal" @click="close_route_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body company-case-route-body">
        <div class="company-case-modal-label text-uppercase mb-2">Priority</div>
        <div v-if="route_status_options.length">
          <label
            v-for="status in route_status_options"
            :key="status.id"
            class="case-details-decision-option company-case-route-option mb-2"
            :class="{ 'is-selected': String(route_priority) === String(status.id) }"
          >
            <input
              v-model="route_priority"
              class="case-details-decision-radio"
              type="radio"
              :value="String(status.id)"
              :disabled="route_loading || submitting_route_case"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">
                {{ status.label }}
                <span v-if="status.is_final" class="case-details-decision-badge">Final</span>
              </span>
            </span>
          </label>
        </div>

        <div class="company-case-modal-label text-uppercase mb-2">Route to</div>
        <div v-if="route_destination_options.length">
          <label
            v-for="role in route_destination_options"
            :key="role.id"
            class="case-details-decision-option company-case-route-option mb-2"
            :class="{ 'is-selected': String(route_destination) === String(role.id) }"
          >
            <input
              v-model="route_destination"
              class="case-details-decision-radio"
              type="radio"
              :value="String(role.id)"
              :disabled="route_loading || submitting_route_case"
              @change="sync_route_group_selection"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">{{ role.label }}</span>
              <span v-if="role.group_name" class="case-details-decision-option-body">{{ role.group_name }}</span>
            </span>
          </label>
        </div>

        <div v-if="selected_route_role" class="mt-3">
          <div class="company-case-modal-label text-uppercase mb-2">Role group</div>
          <select
            v-model="route_group"
            class="form-select company-case-modal-control"
            :disabled="route_loading || submitting_route_case || !available_route_groups.length"
            @change="sync_route_user_selection"
          >
            <option value="" disabled>Select a role group</option>
            <option v-for="group in available_route_groups" :key="group.id" :value="String(group.id)">
              {{ group.label }}
            </option>
          </select>
        </div>

        <div v-if="selected_route_group" class="mt-3">
          <div class="company-case-modal-label text-uppercase mb-2">Users</div>
          <label class="case-details-decision-option company-case-route-option mb-2" :class="{ 'is-selected': route_select_all_users }">
            <input
              v-model="route_select_all_users"
              class="case-details-decision-radio"
              type="checkbox"
              :disabled="route_loading || submitting_route_case || !available_route_users.length"
              @change="toggle_all_route_users"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span class="case-details-decision-option-title">All users in this role group</span>
          </label>

          <label
            v-for="user in available_route_users"
            :key="user.user_id"
            class="case-details-decision-option company-case-route-option mb-2"
            :class="{ 'is-selected': route_selected_user_ids.includes(String(user.user_id)) }"
          >
            <input
              v-model="route_selected_user_ids"
              class="case-details-decision-radio"
              type="checkbox"
              :value="String(user.user_id)"
              :disabled="route_loading || submitting_route_case"
              @change="sync_route_user_selection"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">{{ user.name }}</span>
              <span class="case-details-decision-option-body">{{ assignment_label(user) }}</span>
            </span>
          </label>

          <div v-if="!available_route_users.length" class="small text-secondary">
            No users were found for the selected role group.
          </div>
        </div>

        <div v-if="route_loading" class="small text-secondary mt-3">
          Loading routing options...
        </div>
        <div v-else-if="route_error" class="small text-danger fw-semibold mt-3">
          {{ route_error }}
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="submitting_route_case" @click="close_route_case_modal">Cancel</button>
        <button type="button" class="btn btn-primary-dark px-4" :disabled="!can_save_route_case" @click="route_selected_case">
          {{ submitting_route_case ? 'Routing...' : 'Route case ->' }}
        </button>
      </footer>
    </section>
  </div>
</div>
